import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useTheme } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ChevronLeft, ChevronRight, CheckCircle, Clock, FileText,
  PlayCircle, Lock, Award, Sparkles, BookOpen, StickyNote, Trash2, Plus,
  Pause, Play, ArrowRight, Check, ShieldCheck, Maximize2, Minimize2, X, Eye,
  ExternalLink, Video, MonitorPlay, Edit3, Copy, CheckCheck
} from "lucide-react";
import { toast } from "sonner";
import DoubtWidget from "@/components/doubt/DoubtWidget";
import { getVideoEmbedInfo } from "@/lib/videoEmbed";

export default function LessonPage() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isLight = theme === "light";

  const [lesson, setLesson] = useState(null);
  const [allLessons, setAllLessons] = useState([]);
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);

  // Theater, zoom framing, and fullscreen states
  const [theaterMode, setTheaterMode] = useState(false);
  const [cropZoomHeader, setCropZoomHeader] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const playerContainerRef = useRef(null);

  // Auto-advance timer state
  const [autoAdvanceCountdown, setAutoAdvanceCountdown] = useState(null);
  const autoAdvanceIntervalRef = useRef(null);

  // Celebration modal state
  const [showCompletionModal, setShowCompletionModal] = useState(false);

  // Notes tab state
  const [activeTab, setActiveTab] = useState("doubts"); // "doubts" | "notes"
  const [notes, setNotes] = useState([]);
  const [newNoteText, setNewNoteText] = useState("");

  // Protected PDF / Document Reader State
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [isFullscreenDoc, setIsFullscreenDoc] = useState(false);

  const iframeRef = useRef(null);
  const videoRef = useRef(null);

  // Sync fullscreen state with browser events
  useEffect(() => {
    const handleFsChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFsChange);
    return () => document.removeEventListener("fullscreenchange", handleFsChange);
  }, []);

  const toggleBrowserFullscreen = () => {
    if (!playerContainerRef.current) return;
    if (!document.fullscreenElement) {
      if (playerContainerRef.current.requestFullscreen) {
        playerContainerRef.current.requestFullscreen().catch(() => {});
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
    }
  };

  const getProtectedViewerUrl = (rawUrl) => {
    if (!rawUrl) return "";
    let url = rawUrl.trim();
    if (url.includes("drive.google.com/file/d/")) {
      return url.replace(/\/(view|edit)(\?.*)?$/, "/preview");
    }
    if (url.includes("docs.google.com") && !url.includes("/preview")) {
      return url.replace(/\/edit(\?.*)?$/, "/preview");
    }
    if (url.toLowerCase().includes(".pdf") || url.includes("/uploads/")) {
      const cleanUrl = url.split("#")[0];
      return `${cleanUrl}#toolbar=0&navpanes=0&scrollbar=1`;
    }
    return url;
  };

  // Load saved notes and auto-saved draft for this lesson
  useEffect(() => {
    if (!lessonId) return;
    try {
      const stored = localStorage.getItem(`jamadhan_notes_${lessonId}`);
      if (stored) {
        setNotes(JSON.parse(stored));
      } else {
        setNotes([]);
      }
      const draft = localStorage.getItem(`jamadhan_notes_draft_${lessonId}`);
      if (draft) {
        setNewNoteText(draft);
      }
    } catch {}
  }, [lessonId]);

  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState("");

  const formatNoteTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const saveNotes = (updated) => {
    setNotes(updated);
    try {
      localStorage.setItem(`jamadhan_notes_${lessonId}`, JSON.stringify(updated));
    } catch {}
  };

  const handleDraftChange = (text) => {
    setNewNoteText(text);
    try {
      if (text.trim()) {
        localStorage.setItem(`jamadhan_notes_draft_${lessonId}`, text);
      } else {
        localStorage.removeItem(`jamadhan_notes_draft_${lessonId}`);
      }
    } catch {}
  };

  const handleAddNote = () => {
    if (!newNoteText.trim()) return;
    let ts = 0;
    if (videoRef?.current && typeof videoRef.current.currentTime === "number") {
      ts = Math.floor(videoRef.current.currentTime);
    }
    const noteObj = {
      id: Date.now().toString(),
      text: newNoteText.trim(),
      timestamp: ts,
      createdAt: new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }),
      date: new Date().toLocaleDateString("en-IN", { month: "short", day: "numeric" }),
    };
    saveNotes([noteObj, ...notes]);
    setNewNoteText("");
    try {
      localStorage.removeItem(`jamadhan_notes_draft_${lessonId}`);
    } catch {}
    toast.success("Note saved!");
  };

  const handleUpdateNote = (id) => {
    if (!editingNoteText.trim()) return;
    const updated = notes.map((n) =>
      n.id === id
        ? {
            ...n,
            text: editingNoteText.trim(),
            updatedAt: new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }),
          }
        : n
    );
    saveNotes(updated);
    setEditingNoteId(null);
    setEditingNoteText("");
    toast.success("Note updated!");
  };

  const handleDeleteNote = (id) => {
    const updated = notes.filter((n) => n.id !== id);
    saveNotes(updated);
    toast.success("Note removed");
  };

  const insertTimestampToDraft = () => {
    let ts = 0;
    if (videoRef?.current && typeof videoRef.current.currentTime === "number") {
      ts = Math.floor(videoRef.current.currentTime);
    }
    const tag = `[⏱ ${formatNoteTime(ts)}] `;
    const updated = newNoteText ? `${newNoteText}\n${tag}` : tag;
    handleDraftChange(updated);
  };

  const seekVideoToTimestamp = (seconds) => {
    if (videoRef?.current && typeof videoRef.current.currentTime === "number") {
      videoRef.current.currentTime = seconds;
      videoRef.current.play?.().catch(() => {});
      toast.info(`Jumped to ${formatNoteTime(seconds)}`);
    }
  };

  const handleCopyAllNotes = () => {
    if (notes.length === 0) return;
    const fullText = notes
      .map(
        (n, i) =>
          `📝 Note ${i + 1} (${n.date || ""} ${n.createdAt || ""}):\n${n.text}\n`
      )
      .join("\n---\n\n");
    navigator.clipboard.writeText(fullText);
    toast.success("All lesson notes copied to clipboard!");
  };

  // Main fetch lesson and sequential course syllabus
  const fetchLessonData = useCallback(async () => {
    try {
      const lessonRes = await axios.get(`${API}/lesson/${lessonId}`, { withCredentials: true });
      const currentLesson = lessonRes.data;
      setLesson(currentLesson);

      let fullSyllabus = [];
      let modulesList = [];
      let courseProg = null;

      if (currentLesson.course_id) {
        // Fetch all ordered course lessons and progress in parallel
        const [allLessonsRes, progressRes] = await Promise.allSettled([
          axios.get(`${API}/courses/${currentLesson.course_id}/all-lessons`, { withCredentials: true }),
          axios.get(`${API}/progress/${currentLesson.course_id}`, { withCredentials: true })
        ]);

        if (allLessonsRes.status === "fulfilled" && allLessonsRes.value.data?.lessons?.length > 0) {
          fullSyllabus = allLessonsRes.value.data.lessons;
          modulesList = allLessonsRes.value.data.modules || [];
        }
        if (progressRes.status === "fulfilled") {
          courseProg = progressRes.value.data;
        }
      }

      // Fallback if course_id wasn't available or all-lessons failed
      if (fullSyllabus.length === 0 && currentLesson.module_id) {
        const modRes = await axios.get(`${API}/lessons/${currentLesson.module_id}`, { withCredentials: true });
        fullSyllabus = modRes.data || [];
      }

      setAllLessons(fullSyllabus);
      setModules(modulesList);
      setProgress(courseProg);
    } catch (error) {
      toast.error("Failed to load lesson details");
    } finally {
      setLoading(false);
    }
  }, [lessonId]);

  useEffect(() => {
    fetchLessonData();
  }, [fetchLessonData]);

  // Clean up auto-advance timer on unmount or navigation
  useEffect(() => {
    return () => {
      if (autoAdvanceIntervalRef.current) {
        clearInterval(autoAdvanceIntervalRef.current);
      }
    };
  }, [lessonId]);

  const isCompleted = (lid) => progress?.completed_lessons?.includes(lid);

  const currentIndex = allLessons.findIndex((l) => l.lesson_id === lessonId);
  const prevLesson = currentIndex > 0 ? allLessons[currentIndex - 1] : null;
  const nextLesson = currentIndex >= 0 && currentIndex < allLessons.length - 1 ? allLessons[currentIndex + 1] : null;

  const completedCount = allLessons.filter(l => isCompleted(l.lesson_id)).length;
  const totalCount = allLessons.length || 1;
  const progressPct = Math.round((completedCount / totalCount) * 100);

  // Trigger auto-advance countdown
  const startAutoAdvance = (nextL) => {
    if (!nextL) return;
    setAutoAdvanceCountdown(4);
    if (autoAdvanceIntervalRef.current) clearInterval(autoAdvanceIntervalRef.current);

    autoAdvanceIntervalRef.current = setInterval(() => {
      setAutoAdvanceCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(autoAdvanceIntervalRef.current);
          navigate(`/lesson/${nextL.lesson_id}`);
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const cancelAutoAdvance = () => {
    if (autoAdvanceIntervalRef.current) clearInterval(autoAdvanceIntervalRef.current);
    setAutoAdvanceCountdown(null);
    toast.info("Auto-advance paused. You can review this lesson at your pace.");
  };

  const markComplete = async () => {
    if (!lesson?.course_id) {
      toast.error("Course reference missing for this lesson");
      return;
    }
    setCompleting(true);
    try {
      const res = await axios.post(`${API}/progress/complete-lesson`, {
        course_id: lesson.course_id,
        lesson_id: lessonId
      }, { withCredentials: true });

      setProgress(res.data);
      const newPct = Math.round(res.data.progress_percentage || 0);

      if (newPct >= 100 || (completedCount + 1 >= allLessons.length)) {
        setShowCompletionModal(true);
        toast.success("🏆 Course 100% Completed! Congratulations!");
      } else {
        toast.success(`Lesson completed! Total course progress: ${newPct}%`);
        if (nextLesson) {
          startAutoAdvance(nextLesson);
        }
      }
    } catch (error) {
      toast.error("Failed to mark lesson as complete");
    } finally {
      setCompleting(false);
    }
  };

  const embedInfo = getVideoEmbedInfo(lesson?.video_url);
  const alreadyCompleted = isCompleted(lessonId);

  if (loading) {
    return (
      <DashboardLayout title="Lesson">
        <div className="flex flex-col items-center justify-center h-80 gap-3">
          <div className="w-10 h-10 border-4 border-[#00FF88] border-t-transparent rounded-full animate-spin" />
          <p className={`text-xs font-jetbrains ${isLight ? "text-slate-500" : "text-gray-400"}`}>Loading lesson...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!lesson) {
    return (
      <DashboardLayout title="Lesson">
        <div className="text-center py-20">
          <Lock className="w-12 h-12 text-[#52525B] mx-auto mb-3" />
          <p className={`font-plex text-base ${isLight ? "text-slate-700" : "text-[#52525B]"}`}>Lesson not found or access restricted.</p>
          <Button onClick={() => navigate(-1)} className="mt-4 bg-sky-600 !text-white font-bold h-9">
            Go Back
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={lesson.title}>
      <div className={`space-y-5 transition-all duration-300 ${theaterMode ? "w-full max-w-none px-0" : "max-w-7xl mx-auto"}`} data-testid="lesson-page">

        {/* ── Auto Advance Banner ─────────────────────────────────── */}
        {autoAdvanceCountdown !== null && nextLesson && (
          <div className={`p-4 rounded-xl border flex items-center justify-between flex-wrap gap-3 shadow-md animate-in slide-in-from-top duration-200 ${
            isLight ? "bg-sky-50 border-sky-300 text-sky-900" : "bg-sky-950/40 border-sky-500/30 text-sky-200"
          }`}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-sky-500/20 flex items-center justify-center font-jetbrains font-bold text-sky-600 dark:text-sky-400 text-sm animate-pulse">
                {autoAdvanceCountdown}s
              </div>
              <div>
                <p className="text-xs font-jetbrains uppercase tracking-wider font-bold text-sky-600 dark:text-sky-400">Next Lesson Starting Soon</p>
                <p className="text-sm font-outfit font-bold">{nextLesson.title}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => {
                  if (autoAdvanceIntervalRef.current) clearInterval(autoAdvanceIntervalRef.current);
                  setAutoAdvanceCountdown(null);
                  navigate(`/lesson/${nextLesson.lesson_id}`);
                }}
                size="sm"
                className="bg-sky-600 hover:bg-sky-700 !text-white font-bold h-8 text-xs"
              >
                Play Next Now <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
              <Button
                onClick={cancelAutoAdvance}
                variant="outline"
                size="sm"
                className={`h-8 text-xs ${isLight ? "border-slate-300 text-slate-700 bg-white hover:bg-slate-100" : "border-white/10 text-white hover:bg-white/5"}`}
              >
                Stay on this lesson
              </Button>
            </div>
          </div>
        )}

        {/* ── Top Course Progress Bar ──────────────────────────────── */}
        <div className={`terminal-card rounded-2xl p-4 shadow-xs border ${isLight ? "bg-white border-slate-200" : ""}`} data-testid="lesson-progress">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <BookOpen className={`w-4 h-4 ${isLight ? "text-emerald-600" : "text-[#00FF88]"}`} />
              <p className={`font-jetbrains text-[10px] tracking-[0.15em] uppercase font-bold ${isLight ? "text-slate-600" : "text-[#00FF88]"}`}>
                {lesson.module_title ? `${lesson.module_title} · ` : ""}Course Progress
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-sm font-bold font-jetbrains ${isLight ? "text-emerald-700" : "text-[#00FF88]"}`}>
                {progressPct}%
              </span>
              {progressPct >= 100 && (
                <Button
                  onClick={() => navigate("/certificates")}
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-600 !text-white font-bold rounded-lg text-xs h-7 px-3 shadow-xs"
                  data-testid="claim-certificate-btn"
                >
                  <Award className="w-3.5 h-3.5 mr-1" /> Claim Certificate
                </Button>
              )}
            </div>
          </div>

          <Progress value={progressPct} className="h-2 bg-slate-100 dark:bg-white/[0.04]" />
          
          <div className="flex items-center justify-between mt-2 text-xs">
            <p className={`font-jetbrains text-[11px] ${isLight ? "text-slate-500" : "text-[#52525B]"}`}>
              <strong className={isLight ? "text-slate-900" : "text-white"}>{completedCount}</strong> of <strong className={isLight ? "text-slate-900" : "text-white"}>{allLessons.length}</strong> lessons completed
            </p>
            {currentIndex >= 0 && (
              <p className={`font-jetbrains text-[11px] ${isLight ? "text-slate-500" : "text-[#52525B]"}`}>
                Current: Lesson {currentIndex + 1} of {allLessons.length}
              </p>
            )}
          </div>
        </div>

        {/* ── Video Player & Screen Controls ───────────────────────── */}
        <div
          ref={playerContainerRef}
          className={`terminal-card rounded-2xl overflow-hidden border border-slate-200 dark:border-white/[0.08] shadow-2xl bg-black transition-all duration-300 ${
            isFullscreen ? "fixed inset-0 z-50 rounded-none border-none p-0 h-screen w-screen flex flex-col justify-center bg-black" : ""
          }`}
          data-testid="video-player"
        >
          {/* Top Video Toolbar: provider badge, theater mode, focus view, fullscreen */}
          {embedInfo.embedUrl && !isFullscreen && (
            <div className={`px-4 py-2.5 border-b flex items-center justify-between flex-wrap gap-2 ${
              isLight ? "bg-slate-50 border-slate-200 text-slate-700" : "bg-zinc-950/90 border-white/[0.06] text-zinc-300"
            }`}>
              {/* Left: Provider Tag & Lesson Type */}
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-jetbrains font-bold border ${embedInfo.badgeClass}`}>
                  <Video className="w-3 h-3" />
                  {embedInfo.label}
                </span>
                {embedInfo.isZoom && (
                  <span className={`hidden sm:inline-flex items-center text-[11px] font-jetbrains font-medium ${isLight ? "text-slate-500" : "text-zinc-400"}`}>
                    HD Screen Share Stream
                  </span>
                )}
              </div>

              {/* Right: Display / Zoom Controls */}
              <div className="flex items-center gap-1.5">
                {embedInfo.isZoom && (
                  <button
                    onClick={() => setCropZoomHeader(!cropZoomHeader)}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-jetbrains transition-all ${
                      cropZoomHeader
                        ? isLight
                          ? "bg-sky-100 text-sky-800 font-bold border border-sky-300 shadow-xs"
                          : "bg-sky-500/20 text-sky-400 font-bold border border-sky-500/30 shadow-xs"
                        : isLight
                          ? "hover:bg-slate-200/70 text-slate-700"
                          : "hover:bg-white/10 text-zinc-300"
                    }`}
                    title={cropZoomHeader ? "Switch to standard Zoom frame" : "Maximize video area and crop Zoom header banner"}
                  >
                    <MonitorPlay className="w-3.5 h-3.5" />
                    <span>{cropZoomHeader ? "Standard View" : "Focus / Cinema Mode"}</span>
                  </button>
                )}

                <button
                  onClick={() => setTheaterMode(!theaterMode)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-jetbrains transition-all ${
                    theaterMode
                      ? isLight
                        ? "bg-emerald-100 text-emerald-800 font-bold border border-emerald-300 shadow-xs"
                        : "bg-emerald-500/20 text-[#00FF88] font-bold border border-emerald-500/30 shadow-xs"
                      : isLight
                        ? "hover:bg-slate-200/70 text-slate-700"
                        : "hover:bg-white/10 text-zinc-300"
                  }`}
                  title={theaterMode ? "Exit Theater Mode" : "Expand video width to full page"}
                >
                  {theaterMode ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
                  <span>{theaterMode ? "Normal Width" : "Theater Mode"}</span>
                </button>

                <button
                  onClick={toggleBrowserFullscreen}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-jetbrains transition-all ${
                    isLight ? "hover:bg-slate-200/70 text-slate-700" : "hover:bg-white/10 text-zinc-300"
                  }`}
                  title="Full Screen Video (Native)"
                >
                  <Maximize2 className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Fullscreen</span>
                </button>

                {embedInfo.embedUrl && (
                  <a
                    href={embedInfo.embedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-jetbrains transition-all ${
                      isLight ? "hover:bg-slate-200/70 text-slate-700" : "hover:bg-white/10 text-zinc-300"
                    }`}
                    title="Open recording stream in dedicated high-res tab"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    <span className="hidden md:inline">Open Tab</span>
                  </a>
                )}
              </div>
            </div>
          )}

          {embedInfo.embedUrl ? (
            <div className={`relative w-full overflow-hidden transition-all duration-300 ${
              isFullscreen
                ? "h-full w-full"
                : embedInfo.isZoom || embedInfo.provider === "generic" || embedInfo.provider === "iframe"
                  ? theaterMode
                    ? "h-[84vh] min-h-[680px] lg:min-h-[820px] max-h-[960px]"
                    : "h-[74vh] min-h-[580px] md:min-h-[660px] lg:min-h-[750px] max-h-[880px]"
                  : theaterMode
                    ? "h-[78vh] min-h-[600px] w-full"
                    : "aspect-video"
            }`}>
              {embedInfo.provider === "direct" ? (
                <video
                  ref={videoRef}
                  src={embedInfo.embedUrl}
                  controls
                  controlsList="nodownload"
                  onEnded={() => {
                    if (!alreadyCompleted) {
                      toast.info("Video finished! Mark as complete to record your progress.");
                    }
                  }}
                  className="absolute inset-0 w-full h-full object-contain bg-black"
                />
              ) : (
                <iframe
                  ref={iframeRef}
                  src={embedInfo.embedUrl}
                  title={lesson.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                  allowFullScreen
                  className={`absolute inset-x-0 w-full border-0 transition-all duration-200 ${
                    embedInfo.isZoom && cropZoomHeader
                      ? "-top-[64px] h-[calc(100%+64px)]"
                      : "inset-0 h-full"
                  }`}
                  data-testid="lesson-video-iframe"
                />
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-32 bg-slate-900 text-center">
              <div>
                <PlayCircle className="w-12 h-12 text-[#52525B] mx-auto mb-3" />
                <p className="text-[#94A3B8] font-plex text-sm">No video stream linked for this lesson</p>
              </div>
            </div>
          )}
        </div>

        {/* ── Next & Previous Navigation Bar ──────────────────────── */}
        <div className={`terminal-card rounded-2xl p-4 border shadow-xs flex items-center justify-between flex-wrap gap-3 ${
          isLight ? "bg-white border-slate-200" : ""
        }`} data-testid="lesson-navigation">
          
          {/* Previous Button */}
          <button
            onClick={() => prevLesson && navigate(`/lesson/${prevLesson.lesson_id}`)}
            disabled={!prevLesson}
            className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl border transition-all text-left max-w-xs ${
              prevLesson
                ? isLight
                  ? "bg-slate-50 hover:bg-slate-100 border-slate-200 text-slate-800"
                  : "bg-white/[0.02] hover:bg-white/[0.06] border-white/10 text-white"
                : "opacity-40 cursor-not-allowed border-transparent"
            }`}
            data-testid="prev-lesson-btn"
          >
            <ChevronLeft className="w-5 h-5 shrink-0" />
            <div className="truncate">
              <span className={`text-[10px] font-jetbrains uppercase tracking-wider block font-bold ${isLight ? "text-slate-500" : "text-gray-400"}`}>
                Previous Lesson
              </span>
              <span className="text-xs font-outfit font-semibold truncate block">
                {prevLesson ? prevLesson.title : "None (First Lesson)"}
              </span>
            </div>
          </button>

          {/* Quick Mark Complete Button in Navigation Bar */}
          <div className="flex items-center gap-2">
            {alreadyCompleted ? (
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-jetbrains font-bold ${
                isLight ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
              }`}>
                <CheckCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                Completed
              </div>
            ) : (
              <Button
                onClick={markComplete}
                disabled={completing}
                className="bg-emerald-600 hover:bg-emerald-700 !text-white font-bold rounded-xl h-10 px-5 text-xs shadow-xs"
                data-testid="mark-complete-btn"
              >
                {completing ? (
                  "Completing..."
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-1.5" />
                    Mark Lesson Complete
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Next Button */}
          <button
            onClick={() => nextLesson && navigate(`/lesson/${nextLesson.lesson_id}`)}
            disabled={!nextLesson}
            className={`flex items-center justify-end gap-2.5 px-4 py-2.5 rounded-xl border transition-all text-right max-w-xs ${
              nextLesson
                ? isLight
                  ? "bg-slate-50 hover:bg-slate-100 border-slate-200 text-slate-800"
                  : "bg-white/[0.02] hover:bg-white/[0.06] border-white/10 text-white"
                : "opacity-40 cursor-not-allowed border-transparent"
            }`}
            data-testid="next-lesson-btn"
          >
            <div className="truncate">
              <span className={`text-[10px] font-jetbrains uppercase tracking-wider block font-bold ${isLight ? "text-slate-500" : "text-gray-400"}`}>
                Next Lesson
              </span>
              <span className="text-xs font-outfit font-semibold truncate block">
                {nextLesson ? nextLesson.title : "Course Completed"}
              </span>
            </div>
            <ChevronRight className="w-5 h-5 shrink-0" />
          </button>
        </div>

        {/* ── Interactive Doubt Centre & Personal Study Notes ─────── */}
        <div className="space-y-3">
          {/* Tabs header */}
          <div className="flex items-center gap-2 border-b border-slate-200 dark:border-white/10 pb-2">
            <button
              onClick={() => setActiveTab("doubts")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-outfit font-semibold transition-all ${
                activeTab === "doubts"
                  ? isLight
                    ? "bg-purple-100 text-purple-900 font-bold"
                    : "bg-[#A855F7]/20 text-[#A855F7] font-bold"
                  : isLight
                    ? "text-slate-600 hover:text-slate-900"
                    : "text-gray-400 hover:text-white"
              }`}
            >
              Ask Doubt to Mentor
            </button>
            <button
              onClick={() => setActiveTab("notes")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-outfit font-semibold transition-all ${
                activeTab === "notes"
                  ? isLight
                    ? "bg-amber-100 text-amber-900 font-bold"
                    : "bg-[#FFCC00]/20 text-[#FFCC00] font-bold"
                  : isLight
                    ? "text-slate-600 hover:text-slate-900"
                    : "text-gray-400 hover:text-white"
              }`}
            >
              <StickyNote className="w-3.5 h-3.5" />
              My Personal Study Notes ({notes.length})
            </button>
          </div>

          {activeTab === "doubts" ? (
            <DoubtWidget lesson={lesson} iframeRef={iframeRef} videoRef={videoRef} />
          ) : (
            <div className={`terminal-card rounded-2xl p-5 border shadow-xs space-y-4 ${isLight ? "bg-white border-slate-200" : ""}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h3 className={`text-sm font-bold font-outfit ${isLight ? "text-slate-900" : "text-white"}`}>
                    Personal Lesson Notes
                  </h3>
                  <p className={`text-xs ${isLight ? "text-slate-500" : "text-gray-400"}`}>
                    Write detailed notes, trade setups & formulas in full paragraphs. Drafts and notes are saved automatically to your browser.
                  </p>
                </div>
                {notes.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCopyAllNotes}
                    className={`text-xs h-8 px-2.5 rounded-lg flex items-center gap-1.5 transition-colors ${
                      isLight
                        ? "text-slate-600 hover:text-slate-900 hover:bg-slate-100 border border-slate-200"
                        : "text-zinc-400 hover:text-white hover:bg-white/5 border border-white/5"
                    }`}
                    title="Copy all notes for this lesson to clipboard"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    <span>Copy All Notes ({notes.length})</span>
                  </Button>
                )}
              </div>

              {/* Rich Paragraph Notepad */}
              <div
                className={`rounded-xl border p-3.5 transition-all focus-within:ring-2 focus-within:ring-amber-500/30 ${
                  isLight
                    ? "bg-slate-50 border-slate-300 focus-within:border-amber-500"
                    : "bg-white/[0.03] border-white/10 focus-within:border-amber-400"
                }`}
              >
                <textarea
                  rows={4}
                  value={newNoteText}
                  onChange={(e) => handleDraftChange(e.target.value)}
                  onKeyDown={(e) => {
                    // Cmd + Enter (Mac) or Ctrl + Enter (Win) saves the note
                    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                      e.preventDefault();
                      handleAddNote();
                    }
                  }}
                  placeholder="Write comprehensive notes in full paragraphs, key rules, checklists, or setups...&#10;&#10;• Press Enter freely to make new lines and paragraphs.&#10;• Press Cmd/Ctrl + Enter to save quickly."
                  className={`w-full text-xs leading-relaxed bg-transparent resize-y min-h-[96px] focus:outline-none placeholder:text-zinc-500 font-sans ${
                    isLight ? "text-slate-900" : "text-white"
                  }`}
                />

                {/* Notepad Toolbar Footer */}
                <div className="flex items-center justify-between pt-2.5 mt-1 border-t border-white/5 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={insertTimestampToDraft}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-mono font-medium transition-colors cursor-pointer ${
                        isLight
                          ? "bg-slate-200/70 hover:bg-slate-300 text-slate-700"
                          : "bg-white/5 hover:bg-white/10 text-zinc-300 hover:text-white"
                      }`}
                      title="Insert current video timestamp"
                    >
                      <Clock className="w-3 h-3 text-amber-400" />
                      <span>Tag Video Time</span>
                    </button>

                    {newNoteText.trim() && (
                      <span className="text-[10px] text-zinc-500 font-mono hidden sm:inline">
                        • Draft auto-saved
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2.5">
                    <span className="text-[10px] text-zinc-500 hidden sm:inline font-mono">
                      Press Enter for new line • Cmd+Enter to save
                    </span>
                    <Button
                      onClick={handleAddNote}
                      disabled={!newNoteText.trim()}
                      size="sm"
                      className="bg-amber-500 hover:bg-amber-600 !text-white font-bold text-xs h-8 px-4 rounded-lg shadow-sm transition-all"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" /> Save Note
                    </Button>
                  </div>
                </div>
              </div>

              {/* Notes List */}
              <div className="space-y-2.5">
                {notes.length === 0 ? (
                  <p className={`text-xs font-jetbrains py-4 text-center ${isLight ? "text-slate-400" : "text-zinc-600"}`}>
                    No notes taken for this lesson yet. Type above in full paragraphs to record key concepts!
                  </p>
                ) : (
                  notes.map((n) => (
                    <div
                      key={n.id}
                      className={`p-4 rounded-xl border transition-all ${
                        isLight ? "bg-slate-50 border-slate-200" : "bg-white/[0.02] border-white/5"
                      }`}
                    >
                      {editingNoteId === n.id ? (
                        /* Inline Note Editor */
                        <div className="space-y-2.5">
                          <textarea
                            rows={4}
                            value={editingNoteText}
                            onChange={(e) => setEditingNoteText(e.target.value)}
                            className={`w-full text-xs leading-relaxed p-3 rounded-lg border focus:outline-none font-sans resize-y ${
                              isLight
                                ? "bg-white border-amber-400 text-slate-900"
                                : "bg-black/40 border-amber-400 text-white"
                            }`}
                          />
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingNoteId(null);
                                setEditingNoteText("");
                              }}
                              className="text-xs h-7 px-2.5 rounded-md text-zinc-400 hover:text-white"
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleUpdateNote(n.id)}
                              disabled={!editingNoteText.trim()}
                              className="bg-amber-500 hover:bg-amber-600 !text-white font-semibold text-xs h-7 px-3 rounded-md"
                            >
                              Save Changes
                            </Button>
                          </div>
                        </div>
                      ) : (
                        /* Note Card with Paragraph Formatting */
                        <div>
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              {typeof n.timestamp === "number" && n.timestamp > 0 && (
                                <button
                                  type="button"
                                  onClick={() => seekVideoToTimestamp(n.timestamp)}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/30 transition-colors cursor-pointer"
                                  title="Click to jump video to this moment"
                                >
                                  <Clock className="w-2.5 h-2.5" />
                                  {formatNoteTime(n.timestamp)}
                                </button>
                              )}
                              <span className={`text-[10px] font-jetbrains ${isLight ? "text-slate-400" : "text-zinc-500"}`}>
                                {n.date ? `${n.date} at ` : "Saved at "}{n.createdAt}
                              </span>
                              {n.updatedAt && (
                                <span className="text-[10px] text-zinc-500 font-mono italic">
                                  (edited {n.updatedAt})
                                </span>
                              )}
                            </div>

                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => {
                                  setEditingNoteId(n.id);
                                  setEditingNoteText(n.text);
                                }}
                                className="text-slate-400 hover:text-amber-400 p-1 rounded-md hover:bg-white/5 transition-colors cursor-pointer"
                                title="Edit note"
                              >
                                <Edit3 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleDeleteNote(n.id)}
                                className="text-slate-400 hover:text-rose-500 p-1 rounded-md hover:bg-rose-500/10 transition-colors cursor-pointer"
                                title="Delete note"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>

                          <div
                            className={`text-xs leading-relaxed whitespace-pre-wrap break-words font-sans ${
                              isLight ? "text-slate-800" : "text-zinc-200"
                            }`}
                          >
                            {n.text}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Main Content & Course Syllabus Sidebar ──────────────── */}
        <div className="flex flex-col lg:flex-row gap-5">
          {/* Main Content Details */}
          <div className="flex-1 space-y-4">
            <div className={`terminal-card rounded-2xl p-6 border shadow-xs ${isLight ? "bg-white border-slate-200" : ""}`} data-testid="lesson-info">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <h1 className={`text-xl md:text-2xl font-bold font-outfit ${isLight ? "text-slate-900" : "text-white"}`}>
                    {lesson.title}
                  </h1>
                  <div className="flex items-center gap-2 flex-wrap mt-2">
                    {lesson.module_title && (
                      <Badge className={`border-0 text-xs font-jetbrains font-semibold ${isLight ? "bg-slate-100 text-slate-700" : "bg-white/10 text-white"}`}>
                        {lesson.module_title}
                      </Badge>
                    )}
                    <Badge className={`border-0 text-xs font-jetbrains font-semibold ${isLight ? "bg-sky-100 text-sky-800" : "bg-[#00F0FF]/10 text-[#00F0FF]"}`}>
                      <Clock className="w-3 h-3 mr-1" /> {lesson.duration_minutes || 0} min
                    </Badge>
                    {lesson.is_preview && (
                      <Badge className={`border-0 text-xs font-jetbrains font-semibold ${isLight ? "bg-emerald-100 text-emerald-800" : "bg-[#00FF88]/10 text-[#00FF88]"}`}>
                        FREE PREVIEW
                      </Badge>
                    )}
                    {alreadyCompleted && (
                      <Badge className={`border-0 text-xs font-jetbrains font-semibold ${isLight ? "bg-emerald-100 text-emerald-800" : "bg-[#00FF88]/10 text-[#00FF88]"}`}>
                        <CheckCircle className="w-3 h-3 mr-1" /> COMPLETED
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {lesson.description && (
                <div className="pt-2 border-t border-slate-100 dark:border-white/5">
                  <p className={`text-sm font-plex leading-relaxed ${isLight ? "text-slate-600" : "text-[#CBD5E1]"}`}>
                    {lesson.description}
                  </p>
                </div>
              )}
            </div>

            {/* Resources / Protected Study Notes Section */}
            {(() => {
              const availableResources = [];
              if (lesson?.notes_url) {
                availableResources.push({
                  name: lesson.notes_title || "Lesson Study Notes & Slides",
                  url: lesson.notes_url,
                  type: lesson.notes_type || "notes"
                });
              }
              if (Array.isArray(lesson?.resources)) {
                lesson.resources.forEach((r, idx) => {
                  if (r?.url && r.url !== lesson?.notes_url) {
                    availableResources.push({
                      name: r.name || r.title || `Resource ${idx + 1}`,
                      url: r.url,
                      type: r.type || "resource"
                    });
                  }
                });
              }

              if (availableResources.length === 0) return null;

              return (
                <div className={`terminal-card rounded-2xl p-5 sm:p-6 border shadow-xs ${isLight ? "bg-white border-slate-200" : ""}`} data-testid="lesson-resources">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <p className={`font-jetbrains text-[10px] tracking-[0.2em] uppercase font-bold ${isLight ? "text-amber-600" : "text-[#FFCC00]"}`}>
                        STUDY NOTES & ATTACHMENTS
                      </p>
                      <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[10px] font-mono font-medium flex items-center gap-1">
                        <Lock className="w-2.5 h-2.5" /> View Only
                      </Badge>
                    </div>
                    <span className="text-[10px] text-zinc-500 font-mono">Protected In-App Reader</span>
                  </div>

                  <div className="space-y-2.5">
                    {availableResources.map((resource, idx) => (
                      <div
                        key={resource.url || idx}
                        onClick={() => setSelectedDoc(resource)}
                        className={`flex items-center justify-between gap-3 p-3.5 rounded-xl border cursor-pointer transition-all group ${
                          isLight 
                            ? "bg-slate-50 hover:bg-emerald-50/50 border-slate-200 hover:border-emerald-300" 
                            : "bg-white/[0.02] hover:bg-emerald-500/[0.06] border-white/5 hover:border-emerald-500/30"
                        }`}
                        role="button"
                        tabIndex={0}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 shrink-0 group-hover:scale-105 transition-transform">
                            <FileText className="w-4 h-4" />
                          </div>
                          <div className="min-w-0">
                            <span className={`text-sm font-plex font-medium truncate block ${isLight ? "text-slate-800" : "text-white"}`}>
                              {resource.name}
                            </span>
                            <span className="text-[11px] text-zinc-400 font-mono flex items-center gap-1">
                              <Lock className="w-3 h-3 text-zinc-500" /> Click to open document reader (download disabled)
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 px-3 rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 text-xs font-medium font-mono flex items-center gap-1.5"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            <span>View PDF</span>
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Right Sidebar - Full Course Syllabus */}
          <div className="w-full lg:w-80 shrink-0">
            <div className={`terminal-card rounded-2xl p-4 border shadow-xs space-y-3 ${isLight ? "bg-white border-slate-200" : ""}`} data-testid="lesson-list">
              <div className="flex items-center justify-between">
                <p className={`font-jetbrains text-[10px] tracking-[0.15em] uppercase font-bold ${isLight ? "text-sky-700" : "text-[#00F0FF]"}`}>
                  COURSE SYLLABUS ({allLessons.length})
                </p>
                <span className={`text-xs font-jetbrains font-bold ${isLight ? "text-slate-600" : "text-gray-400"}`}>
                  {completedCount}/{allLessons.length}
                </span>
              </div>

              <div className="space-y-1.5 max-h-[600px] overflow-y-auto custom-scrollbar pr-1">
                {allLessons.map((l, idx) => {
                  const done = isCompleted(l.lesson_id);
                  const active = l.lesson_id === lessonId;

                  return (
                    <button
                      key={l.lesson_id}
                      onClick={() => navigate(`/lesson/${l.lesson_id}`)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all border ${
                        active
                          ? isLight
                            ? "bg-sky-50 border-sky-300 shadow-xs"
                            : "bg-[#00F0FF]/15 border-[#00F0FF]/30 shadow-[0_0_12px_rgba(0,240,255,0.15)]"
                          : done
                            ? isLight
                              ? "bg-emerald-50/50 border-emerald-100 hover:bg-emerald-50"
                              : "bg-[#00FF88]/[0.04] border-white/[0.04] hover:bg-[#00FF88]/[0.08]"
                            : isLight
                              ? "bg-white border-slate-100 hover:bg-slate-50"
                              : "bg-white/[0.02] border-white/[0.04] hover:bg-white/[0.06]"
                      }`}
                      data-testid={`lesson-nav-${l.lesson_id}`}
                    >
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-jetbrains font-bold shrink-0 ${
                        done
                          ? "bg-emerald-600 !text-white"
                          : active
                            ? "bg-sky-600 !text-white"
                            : isLight ? "bg-slate-200 text-slate-700" : "bg-white/10 text-gray-400"
                      }`}>
                        {done ? <Check className="w-3.5 h-3.5" /> : idx + 1}
                      </span>

                      <div className="flex-1 truncate">
                        <span className={`text-xs font-outfit truncate block ${
                          active
                            ? isLight ? "text-sky-950 font-bold" : "text-white font-bold"
                            : done
                              ? isLight ? "text-emerald-900 font-semibold" : "text-[#00FF88] font-medium"
                              : isLight ? "text-slate-700" : "text-[#A1A1AA]"
                        }`}>
                          {l.title}
                        </span>
                        {l.module_title && (
                          <span className={`text-[10px] font-jetbrains truncate block ${
                            isLight ? "text-slate-400" : "text-zinc-500"
                          }`}>
                            {l.module_title}
                          </span>
                        )}
                      </div>

                      {l.duration_minutes > 0 && (
                        <span className={`text-[10px] font-jetbrains shrink-0 ${
                          isLight ? "text-slate-400" : "text-zinc-500"
                        }`}>
                          {l.duration_minutes}m
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* ── Celebration Modal (100% Completion) ──────────────────── */}
        {showCompletionModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200">
            <div className={`w-full max-w-md rounded-2xl p-6 border shadow-2xl text-center space-y-4 relative overflow-hidden ${
              isLight ? "bg-white border-slate-200 text-slate-900" : "bg-[#0D1117] border-white/10 text-white"
            }`}>
              <div className="w-16 h-16 rounded-full bg-amber-500/20 text-amber-500 flex items-center justify-center mx-auto shadow-[0_0_30px_rgba(245,158,11,0.3)] animate-bounce">
                <Award className="w-9 h-9" />
              </div>

              <div>
                <span className="text-xs font-jetbrains uppercase tracking-widest text-amber-500 font-bold">Course Completed!</span>
                <h2 className="text-2xl font-bold font-outfit mt-1">Outstanding Achievement!</h2>
                <p className={`text-xs font-plex mt-2 ${isLight ? "text-slate-600" : "text-gray-300"}`}>
                  You have successfully completed all lessons in this curriculum. You are now eligible to claim your official certificate of completion.
                </p>
              </div>

              <div className="pt-2 flex flex-col gap-2">
                <Button
                  onClick={() => navigate("/certificates")}
                  className="bg-amber-500 hover:bg-amber-600 !text-white font-bold h-11 text-sm shadow-md"
                >
                  <Award className="w-4 h-4 mr-2" /> Claim Your Certificate
                </Button>
                <Button
                  onClick={() => setShowCompletionModal(false)}
                  variant="outline"
                  className={`h-10 text-xs ${isLight ? "border-slate-300 text-slate-700 hover:bg-slate-100" : "border-white/10 text-white hover:bg-white/5"}`}
                >
                  Continue Reviewing Lessons
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ── Protected PDF / Document Reader Modal (Anti-Download) ── */}
        {selectedDoc && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 md:p-6 bg-black/80 backdrop-blur-md animate-in fade-in duration-200"
            onContextMenu={(e) => {
              e.preventDefault();
              toast.info("Protected study document: download is restricted.");
            }}
          >
            <div className={`flex flex-col w-full ${isFullscreenDoc ? "h-full rounded-none" : "h-[92vh] max-w-6xl rounded-2xl"} bg-[#0D1117] border border-white/10 shadow-2xl overflow-hidden`}>
              {/* Reader Header */}
              <div className="flex items-center justify-between px-4 sm:px-6 py-3.5 border-b border-white/10 bg-slate-900/95 select-none">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 shrink-0">
                    <FileText className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-white truncate">
                      {selectedDoc.name || "Lesson Study Material"}
                    </h3>
                    <div className="flex items-center gap-2 text-[10px] text-zinc-400 font-mono">
                      <span className="flex items-center gap-1 text-emerald-400 font-semibold">
                        <Lock className="w-3 h-3" /> Protected View-Only Mode
                      </span>
                      <span>•</span>
                      <span className="text-zinc-500">Student Copy • Downloads Restricted</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <Badge className="hidden sm:flex bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[10px] font-mono font-medium gap-1">
                    <ShieldCheck className="w-3 h-3" /> DRM Shield Active
                  </Badge>

                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setIsFullscreenDoc(!isFullscreenDoc)}
                    className="text-zinc-400 hover:text-white h-8 w-8 p-0 rounded-lg hover:bg-white/5"
                    title={isFullscreenDoc ? "Exit Fullscreen" : "Fullscreen"}
                  >
                    {isFullscreenDoc ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                  </Button>

                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelectedDoc(null)}
                    className="text-zinc-400 hover:text-rose-400 h-8 w-8 p-0 rounded-lg hover:bg-rose-500/10"
                    title="Close Reader"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Reader Document Iframe */}
              <div className="relative flex-1 w-full h-full bg-[#161B22] overflow-hidden select-none">
                <iframe
                  src={getProtectedViewerUrl(selectedDoc.url)}
                  title={selectedDoc.name || "Study Notes Viewer"}
                  className="w-full h-full border-0 bg-white"
                  onContextMenu={(e) => e.preventDefault()}
                />
                {/* Security watermark footer */}
                <div className="absolute bottom-2.5 right-4 pointer-events-none select-none bg-black/80 backdrop-blur-xs border border-white/10 px-3 py-1 rounded-full text-[10px] font-mono text-zinc-400 flex items-center gap-1.5 shadow-lg">
                  <Lock className="w-3 h-3 text-emerald-400" />
                  <span>Jamadhan Academy • Confidential Student Copy</span>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
}
